import '@fortawesome/fontawesome-free/css/all.css';
import '@fortawesome/fontawesome-free/js/all.js';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

import { useConfigStore } from '@/stores/configsStore';
import { showError } from '@/utils/errorHandler';
import { createPinia } from 'pinia';
import piniaPluginPersistedState from 'pinia-plugin-persistedstate';
import { createApp } from 'vue';
import App from './App.vue';
import { CLIENT_INJECTION_KEY } from './composables/useClient';
import router from './router';
import { MBPClient } from './services/client/api';
import { useAuthStore } from './stores/authStore';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faExpand, faCompress } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

library.add(faExpand, faCompress);

function extractErrorMessage(err: unknown): string {
    if (err instanceof Error) {
        return err.message;
    } else if (typeof err === 'string') {
        return err;
    } else {
        return 'An unexpected error occurred.';
    }
}

async function initializeApp() {
    const app = createApp(App);
    const pinia = createPinia();

    pinia.use(piniaPluginPersistedState);

    app.use(pinia);
    app.use(router);

    const client = new MBPClient('');

    app.provide(CLIENT_INJECTION_KEY, client);

    try {
        const { VUE_APP_API_HOST, VUE_APP_APP_TITLE } = process.env;
        console.log(VUE_APP_API_HOST, VUE_APP_APP_TITLE);
        const configStore = useConfigStore();
        configStore.setApiHost(VUE_APP_API_HOST);
        configStore.setAppTitle(VUE_APP_APP_TITLE);

        client.setHost(VUE_APP_API_HOST || '');

        const authStore = useAuthStore();
        if (authStore.token) {
            console.info(`User was already logged in. Restoring session with token ${authStore.token}...`);
            client.setToken(authStore.token);
            if (!await authStore.checkPermissions()) {
                console.warn('User not authenticated. Forcing logout.')
                router.push('/logout')
            }
            authStore.startSessionCheck();
            authStore.loggedIn = true;
        }

        router.beforeEach((to, from, next) => {
            const appTitle = VUE_APP_APP_TITLE || 'My Vue App';
            const routeName = to.name || '';
            document.title = routeName ? `${String(routeName)} - ${appTitle}` : appTitle;
            next();
        });
    } catch (error) {
        console.error('Initialization error:', error);
        showError('Failed to initialize the application.', extractErrorMessage(error));
    }

    app.config.errorHandler = (err, instance, info) => {
        const errorMessage = extractErrorMessage(err);
        console.error('Global Error:', {
            message: errorMessage,
            instance,
            info,
        });

        showError(
            'An unexpected error occurred.',
            process.env.NODE_ENV === 'development' ? `${errorMessage}\n\n${info}` : null
        );
    };

    app.component('font-awesome-icon', FontAwesomeIcon);

    app.mount('#app');
}

initializeApp();